// Shared parked-conversation context and elapsed-time pacing. Timing never
// creates a spoken turn, changes the agreed plan, or ends/starts a lesson.
export const PARKED_CONVERSATION_SECONDS = 180;
const memoryBudgets = new Map();

export async function loadParkedConversationContext({ lessonId, mode, signal, fetcher = fetch }) {
  if (!lessonId || !["briefing", "debrief"].includes(mode)) throw new Error("The current lesson could not be confirmed.");
  if (signal?.aborted) throw new Error("Conversation cancelled");
  const request = new AbortController();
  const cancel = () => request.abort();
  signal?.addEventListener("abort", cancel, { once: true });
  const timeout = setTimeout(cancel, 15000);
  try {
  const response = await fetcher(`${process.env.REACT_APP_BACKEND_URL || ""}/api/work-mode/lessons/${encodeURIComponent(lessonId)}/conversation-context`, {
    method: "POST", credentials: "include", signal: request.signal,
    headers: { "Content-Type": "application/json" }, body: JSON.stringify({ mode }),
  });
  if (!response.ok) throw new Error("The saved lesson plan could not be checked. Retry while parked.");
  const context = await response.json();
  if (signal?.aborted) throw new Error("Conversation cancelled");
  if (String(context?.lesson_id || "") !== String(lessonId) || context?.mode !== mode
      || !["ready", "missing_snapshot"].includes(context.status)
      || (context.status === "ready" && (!context.snapshot_id || !context.plan))
      || (mode === "briefing" && context.status !== "ready")) {
    throw new Error("The saved plan does not match this lesson. Retry while parked.");
  }
  return context;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", cancel);
  }
}

export function parkedConversationSessionPath(context) {
  const query = new URLSearchParams({ [context.mode]: "1", lesson_id: String(context.lesson_id) });
  if (context.status === "ready") query.set("snapshot_id", context.snapshot_id);
  return `/api/universal_intent/realtime/session?${query}`;
}

export function parkedConversationNotice(context) {
  if (context?.status === "missing_snapshot") return "The opening plan was not captured. Karen will ask you to recall it, without assuming what was agreed.";
  if (context?.plan_changed_since_snapshot) return "The plan changed during the lesson. Karen has the opening plan and the latest saved choices.";
  return "";
}

function browserStorage() {
  try { return typeof window === "undefined" ? null : window.sessionStorage; } catch { return null; }
}

export function conversationBudgetSnapshot(startedAt, now) {
  const elapsedSeconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  return {
    elapsedSeconds, remainingSeconds: Math.max(0, PARKED_CONVERSATION_SECONDS - elapsedSeconds),
    overrunSeconds: Math.max(0, elapsedSeconds - PARKED_CONVERSATION_SECONDS),
    phase: elapsedSeconds >= PARKED_CONVERSATION_SECONDS ? "wrap_up" : elapsedSeconds >= 150 ? "recap" : "conversation",
    targetSeconds: PARKED_CONVERSATION_SECONDS,
  };
}

export function conversationPacingEvent(budget, mode) {
  const direction = budget.phase === "wrap_up"
    ? "The three-minute window has been reached. Let the current human answer and current spoken response finish. On your next natural turn give a brief truthful recap; ask no new topic questions. Do not invent agreement, save anything, or end/start the lesson. The instructor decides when to finish."
    : budget.phase === "recap"
      ? "There are at most thirty seconds left in this three-minute window. Let the current answer finish, then briefly recap the relevant plan and any unresolved point. Avoid opening new topics. Do not invent agreement or rush the pupil."
      : "Keep this parked conversation within its three-minute window, including both Karen and human turns. Ask one short relevant question at a time, listen to the answer, and keep your own replies brief. Timing does not authorise any save, lesson start, or lesson end.";
  return { type: "conversation.item.create", item: { type: "message", role: "system", content: [{
    type: "input_text", text: `NDG elapsed-time pacing for ${mode}: ${budget.elapsedSeconds} seconds elapsed; ${budget.remainingSeconds} seconds remaining. ${direction}`,
  }] } };
}

export function createParkedConversationBudget({ ownerId, lessonId, mode, now = Date.now,
  monotonicNow = () => (typeof performance === "undefined" ? now() : performance.now()), storage = browserStorage(),
  setIntervalFn = setInterval, clearIntervalFn = clearInterval, onUpdate = () => {}, onPacing = () => {} }) {
  if (!ownerId || !lessonId || !["briefing", "debrief"].includes(mode)) throw new Error("The current conversation could not be confirmed.");
  const key = `ndg:parked-conversation:v1:${JSON.stringify([String(ownerId), String(lessonId), mode])}`;
  let state = null, lastMonotonic = null, timer = null, lastPhase = null, stopped = false;
  const retain = () => {
    memoryBudgets.set(key, { ...state });
    try { storage?.setItem(key, JSON.stringify(state)); } catch { /* Memory still preserves a reconnect. */ }
  };
  const tick = () => {
    if (stopped || !state) return;
    const instant = now(), monotonic = monotonicNow();
    // Wall time catches background-tab throttling; monotonic time prevents a
    // clock adjustment from extending the budget or reopening questions.
    state.elapsedMs += Math.max(0, instant - state.observedAt, monotonic - lastMonotonic);
    state.observedAt = instant;
    lastMonotonic = monotonic;
    retain();
    const budget = conversationBudgetSnapshot(0, state.elapsedMs);
    onUpdate(budget);
    if (lastPhase !== budget.phase) {
      // Append context for the next natural turn, never response.create/cancel.
      if (onPacing(conversationPacingEvent(budget, mode)) !== false) lastPhase = budget.phase;
    }
    return budget;
  };
  return {
    start() {
      if (state || stopped) return;
      let saved = memoryBudgets.get(key);
      try {
        const stored = JSON.parse(storage?.getItem(key) || "null");
        if (Number.isFinite(stored?.elapsedMs) && Number.isFinite(stored?.observedAt)
            && (!saved || stored.elapsedMs >= saved.elapsedMs)) saved = stored;
      } catch { /* An unavailable storage surface must not block conversation. */ }
      const instant = now();
      const elapsedMs = saved ? saved.elapsedMs + Math.max(0, instant - saved.observedAt) : 0;
      state = { elapsedMs, observedAt: instant };
      lastMonotonic = monotonicNow();
      tick();
      timer = setIntervalFn(tick, 1000);
    },
    tick,
    stop() { if (!stopped) tick(); stopped = true; if (timer !== null) clearIntervalFn(timer); timer = null; },
  };
}

export function formatConversationBudget(budget) {
  if (!budget) return "Three-minute conversation, including your answers";
  const seconds = budget.phase === "wrap_up" ? budget.overrunSeconds : budget.remainingSeconds;
  const time = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  return budget.phase === "wrap_up" ? `Three minutes reached · ${time} over` : `${time} remaining of 3:00`;
}
